import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { clearSiteIcons } from '../services/siteIcons';
import extensionBridge from '../services/extensionBridge';
import { ExtensionStatusCard, SupportedBrowsersCard, NeverSaveSitesCard } from '../components/BrowserSettingsCards';
import { ActivityLogCard, AuthorizationsCard, ClearLogModal, clearAllActivity } from '../components/DeveloperSettings';
import useAccountSettings from '../hooks/useAccountSettings';
import { apiErrorMessage } from '../utils/apiError';
import { Card, Chip, EmptyState, PillButton, SegmentedControl, Toast, Toggle } from '../ui';
import LanguageSelector from '../components/LanguageSelector';
import { ReactComponent as ChevronLeftIcon } from '../assets/icons/chevron-left.svg';
import { ReactComponent as SlidersIcon } from '../assets/icons/sliders-vertical.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as GlobeIcon } from '../assets/icons/globe.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as TerminalIcon } from '../assets/icons/terminal.svg';
import { ReactComponent as SunIcon } from '../assets/icons/sun.svg';
import { ReactComponent as MoonIcon } from '../assets/icons/moon.svg';
import { ReactComponent as MonitorIcon } from '../assets/icons/monitor.svg';
import { ReactComponent as AlertCircleIcon } from '../assets/icons/alert-circle.svg';
import './AccountSettings.css';

/*
 * Ajustes de la cuenta (plan: design/SETTINGS-DEVELOPER-PRIVACY.md §1). Esquema en el servidor.
 *
 * Lo que hace cada ajuste llega por fases (2-5); hasta entonces su interruptor se muestra deshabilitado
 * con "Coming soon" para que la pantalla no prometa nada que todavia no ocurre. Al implementar el
 * efecto de un ajuste, se quita su ruta de PENDING_EFFECT y el interruptor pasa a ser editable.
 *   Funcionan ya: autofill.suggestPasskeys (la extension), developer.enabled (esta misma pestaña) y,
 *   desde la fase 2, privacy.siteIcons (proxy de iconos) y privacy.watchtower.* (pantalla de Salud).
 *   Desde la fase 3: browser.connectExtension (puente con la extension), autofill.source (filtro en el
 *   servidor), autofill.suggestPasswords y autofill.offerToSave (los aplica la extension).
 *   Desde la fase 4: developer.activityLog (registro local, componentes/DeveloperSettings.js) y
 *   developer.integrations.* (tokens personales: el servidor solo los acepta con la integracion encendida).
 *   Fase 5: los interruptores de CLI y MCP ya tienen su cliente (cli/ y mcp/), y los entornos (tipo de item `environment`,
 *   `GET /api/env/:name`) y las referencias `sgc://` los usan tokens con `env:read` / `secrets:reveal`.
 *   Fase 7: el agente SSH, el escaneo de disco y el servicio local para la CLI existen en la app de ESCRITORIO. Por eso ya no
 *   queda ningun interruptor en PENDING_EFFECT. Son ajustes de la cuenta (se pueden cambiar aqui) pero solo tienen efecto en
 *   esa app: se dice con la etiqueta de DESKTOP_ONLY y una nota debajo.
 */
const PENDING_EFFECT = new Set([]);

// Solo tienen efecto en la app de escritorio (fase 7; browser.desktopApp desde la fase 8: desbloquear la extension con la app).
const DESKTOP_ONLY = new Set(['developer.sshAgent', 'developer.diskScan.sshKeys', 'developer.diskScan.envFiles', 'browser.desktopApp']);

const TABS = [
  { id: 'general', icon: SlidersIcon },
  { id: 'privacy', icon: EyeOffIcon },
  { id: 'browser', icon: GlobeIcon },
  { id: 'autofill', icon: KeyIcon },
  { id: 'developer', icon: TerminalIcon },
];

const getIn = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

// 'a.b.c', v -> { a: { b: { c: v } } }
function toPatch(path, value) {
  return path.split('.').reduceRight((acc, key) => ({ [key]: acc }), value);
}

function Badge({ children }) {
  return <Chip className="sgc-settings__badge" label={children} />;
}

/** Fila con interruptor: titulo + descripcion a la izquierda, interruptor al final. */
function ToggleRow({ title, desc, path, settings, onChange, t }) {
  const soon = PENDING_EFFECT.has(path);
  return (
    <div className="sgc-settings__row">
      <Toggle
        className="sgc-settings__toggle"
        label={(
          <span className="sgc-settings__row-title">
            {title}
            {DESKTOP_ONLY.has(path) && <Badge>{t('settings.desktopOnly')}</Badge>}
            {soon && <Badge>{t('settings.comingSoon')}</Badge>}
          </span>
        )}
        checked={Boolean(getIn(settings, path))}
        disabled={soon}
        onChange={(value) => onChange(path, value)}
      />
      {desc && <p className="sgc-settings__row-desc">{desc}</p>}
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <Card className="sgc-settings__card">
      {title && <h2 className="sgc-settings__card-title">{title}</h2>}
      {desc && <p className="sgc-settings__card-desc">{desc}</p>}
      <div className="sgc-settings__rows">{children}</div>
    </Card>
  );
}

function AccountSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams();
  const { theme, setTheme } = useTheme();
  const { settings, error, reload, update } = useAccountSettings();
  const [toast, setToast] = useState(null);
  const tabsRef = useRef(null);
  // Apagar el registro de actividad (o el modo desarrollador con el registro encendido) lo borra: pide confirmacion.
  const [clearPrompt, setClearPrompt] = useState(null); // { path, value } | null
  const [clearBusy, setClearBusy] = useState(false);
  const [logRefresh, setLogRefresh] = useState(0);
  const [authRefresh, setAuthRefresh] = useState(0);

  // Organizaciones y bóvedas propias para el selector de origen del autocompletado.
  const [orgs, setOrgs] = useState([]);
  const [vaults, setVaults] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get('/org/mine')
      .then((res) => {
        if (cancelled) return;
        setOrgs((res.data?.organizations || [])
          .filter((o) => o.member?.status === 'active')
          .map((o) => ({ id: o.organization.id, name: o.organization.name })));
      })
      .catch(() => { if (!cancelled) setOrgs([]); });
    api.get('/vaults')
      .then((res) => {
        if (cancelled) return;
        setVaults((res.data?.vaults || []).map((v) => ({ id: v.vaultId, name: v.name })));
      })
      .catch(() => { if (!cancelled) setVaults([]); });
    return () => { cancelled = true; };
  }, []);

  const handleChange = useCallback((path, value) => {
    // Apagar el registro de actividad borra lo registrado: antes se pide confirmacion.
    if (value === false && (path === 'developer.activityLog' || (path === 'developer.enabled' && settings?.developer.activityLog))) {
      setClearPrompt({ path, value });
      return;
    }
    // Apagar los iconos libera los que ya se habian descargado en esta pestaña.
    if (path === 'privacy.siteIcons' && value === false) clearSiteIcons();
    update(toPatch(path, value))
      // El puente sigue al ajuste solo cuando la cuenta lo ha aceptado: si el PATCH falla, el
      // interruptor vuelve atras y la conexion con la extension no se toca.
      .then(() => {
        if (path === 'browser.connectExtension') extensionBridge.setConnectionEnabled(value);
        // La extension guarda su propio registro y lee estos ajustes: que no espere a su cache de 5 minutos.
        if (path.startsWith('developer.')) extensionBridge.askExtension('SGC_EXT_REFRESH_SETTINGS');
        if (path === 'developer.activityLog') setLogRefresh((n) => n + 1);
        if (path.startsWith('developer.integrations.') || path === 'developer.enabled') setAuthRefresh((n) => n + 1);
      })
      .catch((err) => {
        setToast({ type: 'error', message: apiErrorMessage(err, t, 'settings.saveError') });
      });
  }, [update, t, settings]);

  const confirmClear = useCallback(async () => {
    if (!clearPrompt) return;
    setClearBusy(true);
    try {
      await update(toPatch(clearPrompt.path, clearPrompt.value));
      await clearAllActivity();
      extensionBridge.askExtension('SGC_EXT_REFRESH_SETTINGS');
      setLogRefresh((n) => n + 1);
      setToast({ type: 'success', message: t('settings.developer.log.cleared') });
    } catch (err) {
      setToast({ type: 'error', message: apiErrorMessage(err, t, 'settings.saveError') });
    } finally {
      setClearBusy(false);
      setClearPrompt(null);
    }
  }, [clearPrompt, update, t]);

  // El ajuste de la cuenta manda sobre lo que recuerde este navegador (cambiado desde otro dispositivo).
  const connectExtension = settings?.browser.connectExtension;
  useEffect(() => {
    if (connectExtension !== undefined) extensionBridge.reconcileConnection(connectExtension);
  }, [connectExtension]);

  const themeOptions = useMemo(() => ([
    { value: 'system', label: t('account.profile.themeSystem'), icon: MonitorIcon },
    { value: 'dark', label: t('account.profile.themeDark'), icon: MoonIcon },
    { value: 'light', label: t('account.profile.themeLight'), icon: SunIcon },
  ]), [t]);

  // En movil la tira de pestañas hace scroll: la activa tiene que quedar a la vista.
  useEffect(() => {
    tabsRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [tab]);

  if (tab && !TABS.some((x) => x.id === tab)) return <Navigate to="/account/settings" replace />;
  const activeTab = tab || 'general';

  const rowProps = { settings, onChange: handleChange, t };

  const sourceValue = settings?.autofill.source || 'all';
  const sourceKnown = sourceValue === 'all' || sourceValue === 'personal'
    || orgs.some((o) => `org:${o.id}` === sourceValue) || vaults.some((v) => `vault:${v.id}` === sourceValue);

  const renderTab = () => {
    // Sin ajustes cargados solo la pestaña General (tema e idioma son locales) es util.
    if (activeTab === 'general') {
      return (
        <Section>
          <div className="sgc-settings__row sgc-settings__row--inline">
            <span className="sgc-settings__row-title">{t('account.profile.theme')}</span>
            <SegmentedControl options={themeOptions} value={theme} onChange={setTheme} />
          </div>
          <div className="sgc-settings__row sgc-settings__row--inline">
            <span className="sgc-settings__row-title">{t('account.profile.language')}</span>
            <LanguageSelector />
          </div>
        </Section>
      );
    }

    if (error) {
      return (
        <Card>
          <EmptyState
            icon={AlertCircleIcon}
            title={t('settings.loadError')}
            actionLabel={t('settings.retry')}
            onAction={reload}
          />
        </Card>
      );
    }
    if (!settings) return <p className="sgc-settings__loading" role="status">{t('common.loading')}</p>;

    if (activeTab === 'privacy') {
      return (
        <>
          <Section>
            <ToggleRow {...rowProps} path="privacy.siteIcons" title={t('settings.privacy.siteIcons.title')} desc={t('settings.privacy.siteIcons.desc')} />
          </Section>
          <Section title={t('settings.privacy.checks.title')} desc={t('settings.privacy.checks.desc')}>
            <p className="sgc-settings__row-desc sgc-settings__note">{t('settings.privacy.checks.local')}</p>
            <ToggleRow {...rowProps} path="privacy.watchtower.breachedSites" title={t('settings.privacy.breachedSites.title')} desc={t('settings.privacy.breachedSites.desc')} />
            <ToggleRow {...rowProps} path="privacy.watchtower.pwnedPasswords" title={t('settings.privacy.pwnedPasswords.title')} desc={t('settings.privacy.pwnedPasswords.desc')} />
            <ToggleRow {...rowProps} path="privacy.watchtower.twoFactorAvailable" title={t('settings.privacy.twoFactorAvailable.title')} desc={t('settings.privacy.twoFactorAvailable.desc')} />
            <ToggleRow {...rowProps} path="privacy.watchtower.passkeysAvailable" title={t('settings.privacy.passkeysAvailable.title')} desc={t('settings.privacy.passkeysAvailable.desc')} />
          </Section>
        </>
      );
    }

    if (activeTab === 'browser') {
      return (
        <>
          <Section>
            <ToggleRow {...rowProps} path="browser.connectExtension" title={t('settings.browser.connectExtension.title')} desc={t('settings.browser.connectExtension.desc')} />
            <ToggleRow {...rowProps} path="browser.desktopApp" title={t('settings.browser.desktopApp.title')} desc={t('settings.browser.desktopApp.desc')} />
            <p className="sgc-settings__row-desc">{t('settings.desktopOnlyNote')}</p>
          </Section>
          <ExtensionStatusCard connectionEnabled={settings.browser.connectExtension} />
          <SupportedBrowsersCard />
        </>
      );
    }

    if (activeTab === 'autofill') {
      return (
        <>
        <Section>
          <ToggleRow {...rowProps} path="autofill.suggestPasskeys" title={t('settings.autofill.suggestPasskeys.title')} desc={t('settings.autofill.suggestPasskeys.desc')} />
          <ToggleRow {...rowProps} path="autofill.suggestPasswords" title={t('settings.autofill.suggestPasswords.title')} desc={t('settings.autofill.suggestPasswords.desc')} />
          <ToggleRow {...rowProps} path="autofill.offerToSave" title={t('settings.autofill.offerToSave.title')} desc={t('settings.autofill.offerToSave.desc')} />
          <div className="sgc-settings__row">
            <label className="sgc-settings__row-title" htmlFor="sgc-settings-source">
              {t('settings.autofill.source.title')}
            </label>
            <p className="sgc-settings__row-desc">{t('settings.autofill.source.desc')}</p>
            <select
              id="sgc-settings-source"
              className="sgc-settings__select"
              value={sourceValue}
              onChange={(e) => handleChange('autofill.source', e.target.value)}
            >
              <option value="all">{t('settings.autofill.source.all')}</option>
              <option value="personal">{t('settings.autofill.source.personal')}</option>
              {orgs.length > 0 && (
                <optgroup label={t('settings.autofill.source.organizations')}>
                  {orgs.map((o) => <option key={o.id} value={`org:${o.id}`}>{o.name}</option>)}
                </optgroup>
              )}
              {vaults.length > 0 && (
                <optgroup label={t('settings.autofill.source.vaults')}>
                  {vaults.map((v) => <option key={v.id} value={`vault:${v.id}`}>{v.name}</option>)}
                </optgroup>
              )}
              {!sourceKnown && <option value={sourceValue}>{sourceValue}</option>}
            </select>
          </div>
        </Section>
        <NeverSaveSitesCard />
        </>
      );
    }

    // developer
    const enabled = Boolean(settings.developer.enabled);
    return (
      <>
        <Section>
          <ToggleRow {...rowProps} path="developer.enabled" title={t('settings.developer.enabled.title')} desc={t('settings.developer.enabled.desc')} />
        </Section>
        {enabled && (
          <>
            <Section>
              <ToggleRow {...rowProps} path="developer.activityLog" title={t('settings.developer.activityLog.title')} desc={t('settings.developer.activityLog.desc')} />
            </Section>
            <ActivityLogCard enabled={Boolean(settings.developer.activityLog)} refreshKey={logRefresh} />
            <Section title={t('settings.developer.integrations.title')} desc={t('settings.developer.integrations.desc')}>
              <ToggleRow {...rowProps} path="developer.integrations.cli" title={t('settings.developer.integrations.cli.title')} desc={t('settings.developer.integrations.cli.desc')} />
              <ToggleRow {...rowProps} path="developer.integrations.sdk" title={t('settings.developer.integrations.sdk.title')} desc={t('settings.developer.integrations.sdk.desc')} />
              <ToggleRow {...rowProps} path="developer.integrations.mcp" title={t('settings.developer.integrations.mcp.title')} desc={t('settings.developer.integrations.mcp.desc')} />
            </Section>
            <AuthorizationsCard integrations={settings.developer.integrations} refreshKey={authRefresh} />
            <Section>
              <ToggleRow {...rowProps} path="developer.sshAgent" title={t('settings.developer.sshAgent.title')} desc={t('settings.developer.sshAgent.desc')} />
              <p className="sgc-settings__row-desc" data-testid="desktop-only-note">{t('settings.desktopOnlyNote')}</p>
            </Section>
            <Section title={t('settings.developer.diskScan.title')}>
              <ToggleRow {...rowProps} path="developer.diskScan.sshKeys" title={t('settings.developer.diskScan.sshKeys.title')} desc={t('settings.developer.diskScan.sshKeys.desc')} />
              <ToggleRow {...rowProps} path="developer.diskScan.envFiles" title={t('settings.developer.diskScan.envFiles.title')} desc={t('settings.developer.diskScan.envFiles.desc')} />
              <p className="sgc-settings__row-desc">{t('settings.desktopOnlyNote')}</p>
            </Section>
          </>
        )}
      </>
    );
  };

  return (
    <div className="sgc-settings">
      <div className="sgc-settings__header">
        <PillButton variant="secondary" size="sm" icon={ChevronLeftIcon} className="sgc-settings__back" onClick={() => navigate('/account')}>
          {t('common.back')}
        </PillButton>
        <h1 className="sgc-settings__title">{t('settings.title')}</h1>
      </div>

      <div className="sgc-settings__layout">
        <nav className="sgc-settings__tabs" ref={tabsRef} aria-label={t('settings.tabsLabel')}>
          {TABS.map(({ id, icon: Icon }) => (
            <Link
              key={id}
              to={`/account/settings/${id}`}
              className={`sgc-settings__tab ${activeTab === id ? 'sgc-settings__tab--active' : ''}`}
              aria-current={activeTab === id ? 'page' : undefined}
            >
              <Icon width={18} height={18} aria-hidden="true" />
              <span>{t(`settings.tab.${id}`)}</span>
            </Link>
          ))}
        </nav>
        <div className="sgc-settings__content">{renderTab()}</div>
      </div>

      <ClearLogModal open={!!clearPrompt} onConfirm={confirmClear} onClose={() => setClearPrompt(null)} busy={clearBusy} />

      {toast && (
        <div className="sgc-settings__toast">
          <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </div>
  );
}

export default AccountSettings;
